import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { ChatContextItem } from "../api";
import { BaseElement } from "../utils/ui-components";
import { getSourcePath } from "../data/types";
import { SearchHit } from "../server/media";
import { aiIcon, playCircleIcon, chevronDownIcon } from "../utils/icons";
import { getRelativeTimeString, formatDuration } from "../utils/utils";
import { AudioState } from "./audio-player";

@customElement("search-hit-view")
export class SearchHitView extends BaseElement {
    @property()
    hit!: SearchHit;

    @property()
    onAudioRequest?: (state: AudioState) => void;

    @property({ type: Boolean })
    inPreview = false;

    @state()
    private isTextLong = false;

    @state()
    private isExpanded = false;

    private textRef?: HTMLElement;

    private playItem = () => {
        this.onAudioRequest?.({
            item: this.hit.item,
        });
    };

    protected updated(): void {
        if (this.textRef) {
            const lineHeight = parseFloat(getComputedStyle(this.textRef).lineHeight);
            const maxHeight = lineHeight * 7;
            this.isTextLong = this.textRef.scrollHeight > maxHeight;
        }
    }

    private addToContext = () => {
        const event = new CustomEvent<ChatContextItem>("add-to-context", {
            detail: {
                type: "item",
                item: this.hit.item,
            },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    };

    render() {
        const { item, matches } = this.hit;
        const mainMatch = matches?.find((m) => m.type == "main");
        const lastSegment = item.segments?.[item.segments.length - 1];
        const duration = lastSegment ? lastSegment.offset + lastSegment.duration : item.duration;
        const text = (mainMatch?.highlights.text || item.text).trim();

        const content = html`
            <!-- Header with vertically centered elements -->
            <div class="flex flex-col gap-2 mb-2">
                <!-- Top row with logo, title, and buttons -->
                <div class="flex items-center gap-3">
                    <img src="/img/${getSourcePath(item.path)}.jpg" alt="" class="w-8 h-8 rounded-md object-cover shrink-0" />
                    <div class="min-w-0 flex-1">
                        <a href=${item.url} target="_blank" rel="noopener noreferrer" class="min-w-0">
                            <span
                                class="font-medium text-gray-900 hover:text-blue-600 transition-colors block
                                line-clamp-2
                                [display:-webkit-box]
                                [-webkit-line-clamp:2]
                                [-webkit-box-orient:vertical]
                                overflow-hidden"
                            >
                                ${unsafeHTML(mainMatch?.highlights.title || item.title)}
                            </span>
                        </a>
                    </div>
                    <div class="flex items-center gap-2 shrink-0">
                        ${!this.inPreview
                            ? html` <button
                                  @click=${this.addToContext}
                                  class="p-1.5 rounded-full hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                                  title="Add to chat context"
                              >
                                  <i class="w-5 h-5 icon">${aiIcon}</i>
                              </button>`
                            : ""}
                        <button
                            @click=${this.playItem}
                            class="p-1.5 rounded-full hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                        >
                            <i class="w-6 h-6 icon">${playCircleIcon}</i>
                        </button>
                    </div>
                </div>

                <!-- Bottom row with date and duration -->
                <div class="flex items-center gap-2 text-sm text-gray-400">
                    <span class="whitespace-nowrap">${getRelativeTimeString(new Date(item.date))}</span>
                    ${duration ? html`<span>•</span><span>${formatDuration(duration)}</span>` : ""}
                </div>
            </div>

            <!-- Content using full width -->
            <div class="${this.isTextLong && !this.isExpanded ? "max-h-[10.5em] overflow-hidden relative" : ""}">
                <div
                    ${ref((el) => (this.textRef = el as HTMLElement))}
                    class="text-sm text-gray-600 leading-[1.5] whitespace-pre-line break-words [overflow-wrap:anywhere]"
                    .innerHTML=${text}
                ></div>
                ${this.isTextLong && !this.isExpanded
                    ? html`
                          <div class="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent"></div>
                          <button
                              @click=${() => {
                                  this.isExpanded = true;
                                  this.requestUpdate();
                              }}
                              class="absolute bottom-0 left-0 right-0 flex justify-center items-center text-gray-400 hover:text-gray-600 transition-colors"
                              aria-label="Show more"
                          >
                              <i class="w-5 h-5 icon">${chevronDownIcon}</i>
                          </button>
                      `
                    : ""}
            </div>

            ${item.segments?.length
                ? html`
                      <segment-list-view
                          .item=${item}
                          .segments=${item.segments}
                          .segmentMatches=${matches}
                          .onAudioRequest=${this.onAudioRequest}
                      ></segment-list-view>
                  `
                : ""}
        `;

        return this.inPreview
            ? content
            : html` <div class="flex flex-col p-4 border border-gray-200 bg-white rounded-lg w-full min-w-0">${content}</div> `;
    }
}
